import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Video, BookMarked, Building2, MessageCircle, HelpCircle, BookOpen, Trophy } from "lucide-react";
import type { AnyRow } from "@/lib/rowTypes";

interface LessonRow {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  lesson_video_url: string | null;
  lesson_infographic_url: string | null;
  decision_prompt_text: string | null;
  completion_takeaway: string | null;
  mastery_principle: string | null;
}

interface CoverageData {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  has_video: boolean;
  has_infographic: boolean;
  has_vocabulary: boolean;
  has_book_insight: boolean;
  has_company_case: boolean;
  has_decision_prompt: boolean;
  has_quiz: boolean;
  has_takeaway: boolean;
}

const COVERAGE_FIELDS = [
  { key: "has_video", label: "Video", icon: Video, color: "text-blue-400" },
  { key: "has_infographic", label: "Visual", icon: BookOpen, color: "text-purple-400" },
  { key: "has_vocabulary", label: "Vocab", icon: BookOpen, color: "text-cyan-400" },
  { key: "has_book_insight", label: "Book", icon: BookMarked, color: "text-amber-400" },
  { key: "has_company_case", label: "Case", icon: Building2, color: "text-emerald-400" },
  { key: "has_decision_prompt", label: "Decide", icon: MessageCircle, color: "text-pink-400" },
  { key: "has_quiz", label: "Quiz", icon: HelpCircle, color: "text-indigo-400" },
  { key: "has_takeaway", label: "Takeaway", icon: Trophy, color: "text-orange-400" },
] as const;

export default function MasteryCoveragePanel() {
  const [coverage, setCoverage] = useState<CoverageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);
  const [filterField, setFilterField] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      // Get all lessons with their mastery columns
      const { data: lessons } = await supabase
        .from("lessons")
        .select("lesson_id, lesson_name, course_name, lesson_video_url, lesson_infographic_url, decision_prompt_text, completion_takeaway, mastery_principle")
        .order("course_name")
        .order("lesson_order");

      if (!lessons) { setLoading(false); return; }

      const lessonIds = lessons.map(l => l.lesson_id);

      // Batch check: vocabulary, book insights, company cases, questions
      const [vocabRes, insightRes, caseRes, questionRes] = await Promise.all([
        supabase.from("lesson_vocabulary").select("lesson_id").in("lesson_id", lessonIds),
        supabase.from("lesson_book_insights").select("lesson_id").in("lesson_id", lessonIds),
        supabase.from("lesson_company_cases").select("lesson_id").in("lesson_id", lessonIds),
        supabase.from("questions").select("lesson_id").in("lesson_id", lessonIds),
      ]);

      const vocabSet = new Set(((vocabRes.data) || []).map((v: AnyRow) => v.lesson_id));
      const insightSet = new Set(((insightRes.data) || []).map((v: AnyRow) => v.lesson_id));
      const caseSet = new Set(((caseRes.data) || []).map((v: AnyRow) => v.lesson_id));
      const questionSet = new Set(((questionRes.data) || []).map((v: AnyRow) => v.lesson_id));

      const coverageData: CoverageData[] = lessons.map((lesson: AnyRow) => ({
        lesson_id: lesson.lesson_id,
        lesson_name: lesson.lesson_name,
        course_name: lesson.course_name,
        has_video: !!lesson.lesson_video_url,
        has_infographic: !!lesson.lesson_infographic_url,
        has_vocabulary: vocabSet.has(lesson.lesson_id),
        has_book_insight: insightSet.has(lesson.lesson_id),
        has_company_case: caseSet.has(lesson.lesson_id),
        has_decision_prompt: !!lesson.decision_prompt_text,
        has_quiz: questionSet.has(lesson.lesson_id),
        has_takeaway: !!lesson.completion_takeaway || !!lesson.mastery_principle,
      }));

      setCoverage(coverageData);
      setLoading(false);
    };

    void load();
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card/60 p-6">
        <p className="text-sm text-muted-foreground animate-pulse">Loading mastery coverage…</p>
      </div>
    );
  }

  // Group by course
  const courseMap = new Map<string, CoverageData[]>();
  coverage.forEach(c => {
    const arr = courseMap.get(c.course_name) || [];
    arr.push(c);
    courseMap.set(c.course_name, arr);
  });

  // Summary stats
  const totalLessons = coverage.length;
  const fieldStats = COVERAGE_FIELDS.map(f => ({
    ...f,
    count: coverage.filter(c => c[f.key as keyof CoverageData]).length,
    pct: Math.round((coverage.filter(c => c[f.key as keyof CoverageData]).length / totalLessons) * 100),
  }));

  const filteredCoverage = filterField
    ? coverage.filter(c => !c[filterField as keyof CoverageData])
    : coverage;

  const filteredCourseMap = new Map<string, CoverageData[]>();
  filteredCoverage.forEach(c => {
    const arr = filteredCourseMap.get(c.course_name) || [];
    arr.push(c);
    filteredCourseMap.set(c.course_name, arr);
  });

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <div className="rounded-xl border border-border bg-card/60 p-4 space-y-3">
        <h3 className="font-heading text-sm font-semibold text-foreground">
          Mastery Content Coverage — {totalLessons} Lessons
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {fieldStats.map(f => {
            const Icon = f.icon;
            const isFiltered = filterField === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilterField(isFiltered ? null : f.key)}
                className={`flex items-center gap-2 rounded-lg border p-2.5 text-left transition-all ${
                  isFiltered
                    ? "border-primary/50 bg-primary/10"
                    : "border-border/50 bg-background/40 hover:border-border"
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${f.color} shrink-0`} />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-heading font-medium text-muted-foreground truncate">{f.label}</p>
                  <p className="text-xs font-heading font-bold text-foreground">
                    {f.count}/{totalLessons}{" "}
                    <span className={`${f.pct === 100 ? "text-emerald-400" : f.pct > 50 ? "text-amber-400" : "text-destructive"}`}>
                      ({f.pct}%)
                    </span>
                  </p>
                </div>
              </button>
            );
          })}
        </div>
        {filterField && (
          <p className="text-xs text-primary font-heading">
            Showing {filteredCoverage.length} lessons missing{" "}
            {COVERAGE_FIELDS.find(f => f.key === filterField)?.label}
          </p>
        )}
      </div>

      {/* Course-by-course breakdown */}
      <div className="space-y-2">
        {Array.from(filteredCourseMap.entries()).map(([course, lessons]) => {
          const isExpanded = expandedCourse === course;
          const allComplete = lessons.every(l =>
            COVERAGE_FIELDS.every(f => l[f.key as keyof CoverageData])
          );

          return (
            <div key={course} className="rounded-xl border border-border/60 bg-card/40 overflow-hidden">
              <button
                onClick={() => setExpandedCourse(isExpanded ? null : course)}
                className="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/30 transition-colors"
              >
                {allComplete ? (
                  <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                )}
                <span className="text-xs font-heading font-semibold text-foreground flex-1 truncate">
                  {course}
                </span>
                <span className="text-[10px] font-body text-muted-foreground">
                  {lessons.length} lesson{lessons.length !== 1 ? "s" : ""}
                </span>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {isExpanded && (
                <div className="border-t border-border/40 max-h-96 overflow-y-auto">
                  <div className="p-2 space-y-1">
                    {/* Header row */}
                    <div className="grid grid-cols-[1fr_repeat(8,2rem)] gap-0.5 px-2 py-1">
                      <span className="text-[9px] font-heading text-muted-foreground uppercase">Lesson</span>
                      {COVERAGE_FIELDS.map(f => (
                        <span key={f.key} className="text-[8px] font-heading text-muted-foreground text-center" title={f.label}>
                          {f.label.slice(0, 3)}
                        </span>
                      ))}
                    </div>
                    {lessons.map(lesson => (
                      <div
                        key={lesson.lesson_id}
                        className="grid grid-cols-[1fr_repeat(8,2rem)] gap-0.5 px-2 py-1.5 rounded-lg hover:bg-muted/20 transition-colors"
                      >
                        <span className="text-[11px] font-body text-foreground/80 truncate" title={lesson.lesson_name}>
                          {lesson.lesson_name}
                        </span>
                        {COVERAGE_FIELDS.map(f => {
                          const has = lesson[f.key as keyof CoverageData];
                          return (
                            <div key={f.key} className="flex items-center justify-center">
                              <div
                                className={`h-2.5 w-2.5 rounded-full ${
                                  has ? "bg-emerald-400/80" : "bg-destructive/40"
                                }`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
